/**
 * TeachingCourses (2026-10-01) — "Môn học của tôi" cho GIẢNG VIÊN: các môn đang
 * phụ trách (`/api/admin/courses/` đã lọc `current_instructor=user` cho
 * instructor). Trước đây trang chỉ đọc danh sách ghi danh (sinh viên) → giảng
 * viên luôn thấy trống.
 */
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Button,
  Card,
  Col,
  Empty,
  Input,
  Pagination,
  Result,
  Row,
  Space,
  Spin,
  Tag,
  Typography,
} from "antd";
import { BookOutlined, EditOutlined, EyeOutlined, TrophyOutlined } from "@ant-design/icons";
import { Link } from "react-router-dom";
import { listAdminCourses } from "@/admin/api/courses";

const { Text } = Typography;
const PAGE_SIZE = 20;

export function TeachingCourses() {
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");

  const { data, isLoading, error } = useQuery({
    queryKey: ["teaching-courses", page, search],
    queryFn: () => listAdminCourses({ page, search: search || undefined, ordering: "code" }),
    placeholderData: (prev) => prev,
  });

  return (
    <Space direction="vertical" size="middle" style={{ width: "100%" }}>
      <Input.Search
        allowClear
        placeholder="Tìm theo mã hoặc tên môn"
        onSearch={(v) => {
          setSearch(v.trim());
          setPage(1);
        }}
        style={{ maxWidth: 360 }}
      />
      {data && <Text type="secondary">Bạn đang phụ trách {data.count} môn học.</Text>}

      {isLoading && (
        <div style={{ textAlign: "center", padding: 64 }}>
          <Spin size="large" />
        </div>
      )}
      {error && <Result status="error" title="Không tải được danh sách môn phụ trách" />}
      {data && data.results.length === 0 && (
        <Empty
          description={
            search ? "Không có môn khớp tìm kiếm." : "Bạn chưa được phân công phụ trách môn nào."
          }
        />
      )}

      {data && data.results.length > 0 && (
        <Row gutter={[16, 16]} data-testid="teaching-courses-grid">
          {data.results.map((c) => (
            <Col xs={24} sm={12} lg={8} key={c.id}>
              <Card
                actions={[
                  <Link key="view" to={`/courses/${c.slug}`}>
                    <Button type="link" icon={<EyeOutlined />}>
                      Xem
                    </Button>
                  </Link>,
                  <Link key="manage" to={`/admin/courses/${c.slug}`}>
                    <Button type="link" icon={<EditOutlined />}>
                      Quản lý
                    </Button>
                  </Link>,
                  <Link key="quizzes" to={`/courses/${c.slug}/quizzes`}>
                    <Button type="link" icon={<TrophyOutlined />}>
                      Bài kiểm tra
                    </Button>
                  </Link>,
                ]}
              >
                <Space direction="vertical" size={4} style={{ width: "100%" }}>
                  <Space wrap>
                    <Tag color="blue">{c.code}</Tag>
                    {!c.is_published && <Tag>Chưa xuất bản</Tag>}
                  </Space>
                  <Text strong style={{ fontSize: 16 }}>
                    <BookOutlined /> {c.name}
                  </Text>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {c.term_names.length > 0 ? `${c.term_names.join(", ")} · ` : ""}
                    {c.credits} tín chỉ · {c.lesson_count} bài
                  </Text>
                </Space>
              </Card>
            </Col>
          ))}
        </Row>
      )}

      {data && data.count > PAGE_SIZE && (
        <Pagination
          current={page}
          pageSize={PAGE_SIZE}
          total={data.count}
          showSizeChanger={false}
          onChange={setPage}
        />
      )}
    </Space>
  );
}
